import { create } from 'zustand';

import type { auth as authContract } from '@terra/contracts';

import { api } from '../lib/api';

/** Roles allowed into the admin console. */
const ADMIN_ROLES = new Set(['SUPER_ADMIN', 'ADMIN_STAFF']);
export const isAdmin = (user: authContract.AuthSessionUser | null) =>
  !!user && user.roles.some((r) => ADMIN_ROLES.has(r));

interface AuthState {
  user: authContract.AuthSessionUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous';
  bootstrap: () => Promise<void>;
  setSession: (res: authContract.AuthResponse) => void;
  logout: () => Promise<void>;
  can: (perm: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'idle',
  bootstrap: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading' });
    try {
      const res = await api.auth.refresh();
      api.setAccessToken(res.tokens.accessToken);
      set({ user: res.user, status: 'authenticated' });
    } catch {
      api.setAccessToken(null);
      set({ user: null, status: 'anonymous' });
    }
  },
  setSession: (res) => {
    api.setAccessToken(res.tokens.accessToken);
    set({ user: res.user, status: 'authenticated' });
  },
  logout: async () => {
    try {
      await api.auth.logout();
    } finally {
      api.setAccessToken(null);
      set({ user: null, status: 'anonymous' });
    }
  },
  can: (perm) => get().user?.permissions.includes(perm) ?? false,
}));

if (typeof window !== 'undefined') {
  window.addEventListener('terra:auth-expired', () =>
    useAuthStore.setState({ user: null, status: 'anonymous' }),
  );
}
